import type { Session } from '@supabase/supabase-js';
import type { Project } from './database.client.types';
import { hasConfirmedEmailIdentity } from '../utils/authHelpers';

type ActivationCandidate = Pick<
    Project,
    'id' | 'mode' | 'pending_activation' | 'deleted_at' | 'paused_at'
>;

export type ActivationRecoveryReason = 'pending_activation';

export function getActivationRecoverySessionKey(session: Session | null): string {
    if (!session) return 'signed-out';

    const authenticationState = hasConfirmedEmailIdentity(session)
        ? 'authenticated'
        : 'anonymous';

    return `${session.user.id}:${authenticationState}`;
}

/**
 * Identifies projects that should be auto-promoted from experimental -> active
 * when we know the owner is authenticated.
 *
 * Activation stays idempotent: verification paths set `pending_activation`,
 * and the FE finalises the mode switch on the next project load.
 */
export function getActivationRecoveryReason(
    project: ActivationCandidate | null | undefined,
    session: Session | null
): ActivationRecoveryReason | null {
    if (!project || !hasConfirmedEmailIdentity(session)) {
        return null;
    }

    if (project.mode !== 'experimental') return null;
    if (project.deleted_at || project.paused_at) return null;

    if (project.pending_activation) {
        return 'pending_activation';
    }

    return null;
}

export function shouldRecoverProjectActivation(
    project: ActivationCandidate | null | undefined,
    session: Session | null
): boolean {
    return getActivationRecoveryReason(project, session) !== null;
}
